import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { capitalize, filter, find, findIndex, toLower } from 'lodash';
import { SearchIcon } from '../../icons/SearchIcon';
import { DatePicker, Input } from '../../components/Input';
import { IconButton } from '../../icons/IconContainer';
import { expandSearch, setFilter } from '../../store/dataSlice';
import { BorderedField, Flex } from '../../components/Flex';
import { SmallCloseIcon } from '../../icons/SmallCloseIcon';
import { Paragraph } from '../../components/Text';
import { Dropdown, Menu } from 'antd';
import { CheckIcon } from '../../icons/CheckIcon';


const SearchBox = ({ onSearch, disabled, searchOptions=[] }) => {
    const dispatch = useDispatch();
    const { context, search, filterOptions } = useSelector(state => state.data);
    const initialState = {
        visible: false,
        activeFilter: null,
        context: "",
        options: []
    };

    const [state, setState] = useState(initialState);

    useEffect(() => {
        if (Array.isArray(searchOptions) && filter(searchOptions, o => findIndex(filterOptions, { key: o.key}) < 0).length) {
            setState({
                ...initialState,
                visible: true,
                options: filter(searchOptions, o => findIndex(filterOptions, { key: o.key}) < 0) });
        } else {
            setState(initialState);
        }
    }, [ search, filterOptions.length ]);

    const getOptionTitle = (key, value) => {
        let option = find(searchOptions, { key: key });
        if (value !== undefined) {
            if (option && option.hasOwnProperty("options")) {
                let optionValue = find(option.options, oo => oo.key == value);
                if (optionValue && optionValue.hasOwnProperty("title")) {
                    return optionValue.title
                }
            };
            return value;
        };

        return (option && option.hasOwnProperty("title"))
            ? option.title
            : null
    };

    const getOptionType = (key) => {
        const { activeFilter } = state;
        if (activeFilter) {
            let option = find(searchOptions, { key: activeFilter });
            if (option && option.hasOwnProperty("type")) {
                return option.type;
            }
        };
        return "string";
    };

    const getOptionValueLen = (key) => {
        const { activeFilter } = state;
        if (activeFilter) {
            let option = find(searchOptions, { key: activeFilter });
            if (option && option.hasOwnProperty("max")) {
                return option.max;
            }
        };
        return 30;
    };

    const onPressEnter = (value) => {
        const { context, activeFilter } = state;
        if (activeFilter && (context || value)) {
            dispatch(setFilter({ key: activeFilter, value: context || value }));
            setState({
                ...initialState,
                options: filter(searchOptions, o => o.key !== activeFilter && findIndex(filterOptions, { key: o.key || activeFilter }) < 0)
            });
        }
    };

    const onOptionSelect = ({ key }) => {
        const { activeFilter } = state;
        let option = find(searchOptions, { key: activeFilter ? activeFilter : key });
        if (activeFilter) {
            dispatch(setFilter({ key: activeFilter, value: option && option.type === "boolean" ? Number(key) : key }));
        } else {
            setState(state => ({
                ...state,
                activeFilter: key,
                visible: option && option.hasOwnProperty("options"),
                options: option && option.hasOwnProperty("options") ? option.options : []
            }))
        };
    };

    const onClearSearch = () => {
        const { activeFilter } = state;
        if (activeFilter) {
            setState({
                ...initialState,
                visible: true,
                options: filter(searchOptions, o => findIndex(filterOptions, { key: o.key}) < 0)
            })
        } else {
            onSearch("");
        }
    };

    const onClose = () => {
        setState(initialState);
        dispatch(expandSearch(false));
    };

    const getPlaceholderWidth = (title) => {
        let width = 320, titleWidth = 0, offset = 16;
        let canvas = document.createElement("canvas"),
        context = canvas.getContext("2d");
        context.font = "400 14px Arial";
        titleWidth = Math.ceil(context.measureText(title).width);
        if (titleWidth > width) titleWidth = width;

        return width - offset - titleWidth;

    };

    const menu = () => {
        const { options, activeFilter } = state;
        return(
            <Menu onClick={onOptionSelect}>
                {options.map(option => (
                    <Menu.Item key={option.key} onMouseDown={(e) => e.preventDefault()}>
                        <Flex gap={{ right: 4 }} align="center">
                            <Paragraph style={{ borderRadius: "4px", padding: "2px 8px", background: activeFilter ? "none" : "rgba(35, 120, 255, 0.08)" }}>
                                {activeFilter ? option.title : capitalize(option.title) + ":"}
                            </Paragraph>
                            {option.placeholder
                                ? <Paragraph nowrap italic color="var(--sp-disabled-text-color)" style={{ width: getPlaceholderWidth(option.title) }} title={option.placeholder}>{option.placeholder}</Paragraph>
                                : null}
                        </Flex>
                    </Menu.Item>
                ))}
            </Menu>
        )
    };


    return(
        (search)
        ? <BorderedField
            background="white"
            width="100%"
            gap={{ right: 12 }}
            position="relative"
            padding={{ top: "4px", bottom: "4px", left: "48px", right: "10px" }}
        >
            <SearchIcon color="var(--sp-text-grey-color)" style={{ position: "absolute", left: "12px", top: "6px" }}/>
            <Flex wrap="wrap" margin={{ left: "-2px", top: "-2px", right: "-2px", bottom: "-2px" }} width="100%">
                {state.activeFilter && context &&
                    <Flex
                        align="center"
                        gap={{ right: 8}}
                        background="rgba(35, 120, 255, 0.08)"
                        style={{ borderRadius: "4px", padding: "4px 8px"}}
                        margin={{ left: "2px", top: "2px", right: "4px", bottom: "2px" }}
                    >
                        <Paragraph>{context}</Paragraph>
                    </Flex>}
                {Array.isArray(filterOptions) && filterOptions.length
                    ? filterOptions.map(option => (
                        <Flex
                            align="center"
                            gap={{ right: 8 }}
                            key={option.key}
                            background="rgba(35, 120, 255, 0.08)"
                            style={{ borderRadius: "4px", padding: "4px 8px"}}
                            margin={{ left: "2px", top: "3px", right: "4px", bottom: "3px" }}
                        >
                            <Paragraph nowrap>{capitalize(getOptionTitle(option.key))}:</Paragraph>
                            <Paragraph bold>{getOptionTitle(option.key, option.value)}</Paragraph>
                            <SmallCloseIcon
                                color="var(--sp-error-color)"
                                hoverColor="var(--sp-error-hover-color)"
                                onClick={() => dispatch(setFilter(option))}
                            />
                        </Flex>
                    ))
                    : null}
                <Flex align="center" flex={1}>
                    {state.activeFilter && <Paragraph style={{ whiteSpace: "nowrap", lineHeight: "19px" }}>{capitalize(getOptionTitle(state.activeFilter))}:</Paragraph>}
                    {state.options.length
                        ? <Dropdown overlay={menu} visible={state.visible}>
                            <Input
                                autoFocus
                                maxLength={getOptionValueLen()}
                                bordered={false}
                                value={state.activeFilter ? state.context : context}
                                onBlur={(e) => setState(state => ({ ...state, visible: false }))}
                                onMouseDown={(e) => setState(state => ({ ...state, visible: true }))}
                                onChange={(e) => state.activeFilter ? state.options.length ? null : setState(state => ({ ...state, context: e.target.value })) : onSearch(toLower(e.target.value))}
                            />
                        </Dropdown>
                        : getOptionType() === "date"
                            ? <DatePicker
                                onChange={(date, dateString) => {
                                    onPressEnter(dateString);
                                }}
                            />
                            : <Input
                                autoFocus
                                maxLength={getOptionValueLen()}
                                bordered={false}
                                onPressEnter={() => onPressEnter()}
                                value={state.activeFilter ? state.context : context}
                                onChange={(e) => state.activeFilter ? setState(state => ({ ...state, context: e.target.value })) : onSearch(toLower(e.target.value))}
                            />}
                    {state.activeFilter !== null && typeof state.context == "string" && state.context.length
                        ? <CheckIcon
                            color="var(--sp-success-light-color)"
                            hoverColor="var(--sp-success-color)"
                            onClick={() => onPressEnter()}
                            style={{ marginRight: "8px" }}
                        />
                        : null}
                    {(state.activeFilter === null && typeof context == "string" && context.length) || (state.activeFilter !== null)
                        ? <SmallCloseIcon
                            color="var(--sp-error-color)"
                            hoverColor="var(--sp-error-hover-color)"
                            onClick={() => onClearSearch()}
                            style={{ marginRight: "12px" }}
                        />
                        : null}
                </Flex>
            </Flex>
            <SmallCloseIcon
                color="var(--sp-label-color)"
                hoverColor="var(--sp-paragraph-color)"
                onClick={() => onClose() }
                style={{ position: "absolute", right: "10px", top: "11px" }}
            />
        </BorderedField>
        : <IconButton disabled={disabled} background="#FFFFFF" onClick={() => dispatch(expandSearch(true))}>
            <SearchIcon color={disabled ? "var(--sp-disabled-text-color)" : "var(--sp-main-color)"} />
        </IconButton>
    );
};

export { SearchBox };